import React from 'react';
import { Helmet } from "react-helmet-async";
import { Gift, Sparkles, Star, Brain, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { FeatureCards } from '@/components/whats-new/FeatureCards';
import { DevelopmentHighlights } from '@/components/whats-new/DevelopmentHighlights';
import { getFeatureStats } from '@/data/whatsNewFeatures';

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const PublicWhatsNew: React.FC = () => {
  const { totalFeatures, featuresByType, featuresByCategory } = getFeatureStats();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>What's New | Hivetivity - Latest Features & Updates</title>
        <meta 
          name="description" 
          content="Discover the latest features and improvements to Hivetivity. See how we're continuously evolving our youth program management platform." 
        />
        <meta 
          name="keywords" 
          content="hivetivity updates, new features, platform improvements, youth program management updates" 
        />
        <link rel="canonical" href="https://hivebubble.com/whats-new" />
      </Helmet>
      
      {/* Top Navigation */}
      <TopNavbar />
      
      {/* Main content */}
      <div className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-background to-muted/20 py-12 md:py-16 lg:py-20">
          <div className="container mx-auto px-4 text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center mb-6 gap-4">
              <div className="p-3 bg-primary/10 rounded-2xl">
                <Gift className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                What's <span className="text-primary drop-shadow-sm">New</span>
              </h1>
            </div>
            
            <p className="text-lg sm:text-xl md:text-2xl text-secondary/80 max-w-3xl mx-auto mb-8 md:mb-10 leading-relaxed px-4">
              Discover the latest features and improvements to your Hivetivity experience.
              We're constantly evolving to serve you better.
            </p>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 md:py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.5 }}
              className="space-y-8 md:space-y-12"
            >
              {/* Development Statistics */}
              <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4 mb-8 md:mb-12">
                <Card>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg flex-shrink-0">
                        <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xl sm:text-2xl font-bold">{totalFeatures}</p>
                        <p className="text-xs text-muted-foreground truncate">Features Released</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg flex-shrink-0">
                        <Star className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xl sm:text-2xl font-bold">{featuresByType.feature || 0}</p>
                        <p className="text-xs text-muted-foreground truncate">New Features</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0">
                        <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xl sm:text-2xl font-bold">{featuresByType.enhancement || 0}</p>
                        <p className="text-xs text-muted-foreground truncate">Enhancements</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg flex-shrink-0">
                        <Brain className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xl sm:text-2xl font-bold">{featuresByCategory.ai || 0}</p>
                        <p className="text-xs text-muted-foreground truncate">AI Features</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Feature Cards */}
              <FeatureCards 
                mobile={true} 
                animationDelay={0.02}
                gridClassName="grid gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3"
              />

              {/* Development Timeline Summary */}
              <DevelopmentHighlights />
            </motion.div>
          </div>
        </section>
        
        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default PublicWhatsNew;